/** Cartão de equipamento compartilhado entre desktop e portal externo; sem consultas ou sessão interna. */
import { Copy, Cpu, Package, Smartphone, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { isStockStatusOperational, stockStatusLabel } from '@/lib/stock-presentation';
import { STOCK_ITEM_LABEL, type StockCandidate } from '@/lib/serviceOrderStockRules';
import { ConnectionStatusDot } from '@/components/data/ConnectionStatusDot';
import { PROVIDER_DASHBOARD_CARD } from '@/components/dashboard/shared/dashboardCardStyles';


/**
 * Função pura: retorna os lados (instalação/remoção) em que o prestador
 * selecionou mais de um kit. Nesses casos a confirmação precisa passar
 * por um pop-up adicional para evitar erro de seleção ao concluir a OS.
 */
export function sidesWithMultipleKits(install: string[], uninstall: string[]): ('install' | 'uninstall')[] {
  // Kits têm chave no formato "kit:<id>" (ver função `key`).
  const kitCount = (keys: string[]) => keys.filter((k) => k.startsWith('kit:')).length;
  const sides: ('install' | 'uninstall')[] = [];
  if (kitCount(install) > 1) sides.push('install');
  if (kitCount(uninstall) > 1) sides.push('uninstall');
  return sides;
}

/**
 * Situação visual compacta de um item ou componente.
 *
 * O kit usa rótulo próprio desta tela: no banco e no módulo Estoque a situação
 * continua "Ativo", mas aqui o prestador vê "Disponível"/"Indisponível",
 * que comunica melhor se o item pode ou não ser usado na conclusão da OS.
 */
function StatusPill({ kind, status, active = true }: {
  kind: 'tracker' | 'chip' | 'kit'; status?: string | null; active?: boolean | null;
}) {
  // Situação operacional (selo verde): chip e kit exigem "Ativo" e o rastreador
  // também aceita "Instalado" (BR-EST-028), estado normal de quem está no veículo.
  const usable = active !== false && isStockStatusOperational(kind, status);
  const label = kind === 'kit'
    ? (usable ? 'Disponível' : 'Indisponível')
    : active === false ? 'Inativo' : stockStatusLabel(kind, status ?? 'unavailable');
  return (
    <Badge
      variant={usable ? 'secondary' : 'destructive'}
      className={cn(
        'h-5 border-0 px-2 text-[10px] font-semibold',
        // Situação operacional em verde com o token semântico `success`; indisponível segue vermelho.
        usable && 'bg-success text-success-foreground',
      )}
    >
      {label}
    </Badge>
  );
}

/**
 * Quadro compacto de um componente do kit (rastreador ou chip): situação,
 * identificador e modelo/plano — ajuda o prestador a conferir o equipamento usado.
 */
function ComponentDetail({ kind, identifier, description, status, active }: {
  kind: 'tracker' | 'chip'; identifier?: string | null; description: string;
  status?: string | null; active?: boolean | null;
}) {
  const Icon = kind === 'tracker' ? Cpu : Smartphone;
  return (
    <div className="flex min-w-0 items-start gap-2 rounded-md bg-muted/40 px-2.5 py-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {kind === 'tracker' ? 'Rastreador' : 'Chip'}
          </span>
          <StatusPill kind={kind} status={status} active={active} />
        </div>
        <p className="break-all text-xs font-semibold tabular-nums text-foreground">{identifier || 'Não vinculado'}</p>
        <p className="break-words text-[11px] text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

/** Cartão único reutilizado nas etapas Remover e Instalar. */
export function StockCandidateCard({ item, checked, onToggle }: {
  item: StockCandidate; checked: boolean; onToggle: () => void;
}) {
  const isKit = item.item_type === 'kit';
  const description = item.item_type === 'tracker'
    ? `Modelo: ${item.tracker_model_name || 'Não informado'}`
    : item.item_type === 'chip'
      ? `Plano: ${item.chip_data_plan_name || 'Não informado'}`
      : null;

  return (
    <label className={cn(
      // Efeito por elevação (sombra) no hover e na seleção, sem trocar a cor da borda
      // — padrão já usado em outros cartões do sistema.
      'block rounded-md border bg-background shadow-sm transition-all duration-200',
      item.eligible ? 'cursor-pointer hover:shadow-md' : 'cursor-not-allowed opacity-65',
      checked && 'shadow-md shadow-primary/20',
    )}>
      <div className="flex items-start gap-3 p-3.5">
        <Checkbox checked={checked} disabled={!item.eligible} onCheckedChange={onToggle}
          aria-label={`Selecionar ${item.label}`} className="mt-1" />
        {/* Kit: ícone solto e colorido, sem moldura. Itens avulsos mantêm a moldura. */}
        {isKit ? (
          <Package className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        ) : (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            {item.item_type === 'tracker' ? <Cpu className="h-4 w-4" /> : <Smartphone className="h-4 w-4" />}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="break-all text-sm font-semibold tabular-nums text-foreground">{item.label}</p>
              <p className="text-[11px] text-muted-foreground">{STOCK_ITEM_LABEL[item.item_type]}</p>
            </div>
            <StatusPill kind={item.item_type} status={item.status} active={item.is_active} />
          </div>
          {!isKit && description && <p className="mt-2 text-xs text-muted-foreground">{description}</p>}
          {/* Kit: dados do rastreador e do chip vinculados (situação, IMEI/ICCID, modelo/plano). */}
          {isKit && (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <ComponentDetail kind="tracker" identifier={item.tracker_imei}
                description={`Modelo: ${item.tracker_model_name || 'Não informado'}`}
                status={item.tracker_status} active={item.tracker_is_active} />
              <ComponentDetail kind="chip" identifier={item.chip_iccid}
                description={`Plano: ${item.chip_data_plan_name || 'Não informado'}${item.chip_line_number ? ` · Linha: ${item.chip_line_number}` : ''}`}
                status={item.chip_status} active={item.chip_is_active} />
            </div>
          )}
          {!item.eligible && (
            <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-destructive">
              <XCircle className="h-3.5 w-3.5 shrink-0" />
              <span>{item.blocked_reason || 'Item indisponível para movimentação'}</span>
            </div>
          )}
        </div>
      </div>
    </label>
  );
}

/** Variante somente consulta: distingue situação persistida de elegibilidade de instalação. */
export interface StockInventoryItem {
  id: string; kind: 'kit' | 'tracker' | 'chip'; code?: string; status?: string | null;
  imei?: string | null; iccid?: string | null; tracker_status?: string | null; chip_status?: string | null;
  tracker_model_name?: string | null; chip_data_plan_name?: string | null;
  carrier_name?: string | null; line_number?: string | null; since?: string | null;
}

/** Identificador compacto com cópia explícita, sem disparar uma movimentação de estoque. */
function InventoryIdentifier({ label, value, className }: { label: string; value?: string | null; className?: string }) {
  const copy = async () => {
    if (!value) return;
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copiado`); }
    catch { toast.error('Não foi possível copiar o identificador.'); }
  };
  return <span className={cn('flex min-w-0 items-center gap-1', className)}><span className="min-w-0 break-all font-mono text-sm font-medium tabular-nums">{value || `${label} não informado`}</span>{value && <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-muted-foreground" onClick={copy} aria-label={`Copiar ${label}`} title={`Copiar ${label}`}><Copy className="h-3.5 w-3.5" /></Button>}</span>;
}

/** Data curta da última movimentação, no fuso operacional. */
const inventoryDate = (v?: string | null) => v ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(v)) : null;

/**
 * Ficha compacta de estoque (somente consulta), sem dependência de autenticação.
 * RELEASE: 2026-10-12 v2.1.0 - Situação (bolinha + palavra) em linha própria abaixo do identificador em
 *            rastreador/chip, igual referência do app; blocos de rastreador/chip do kit seguem o mesmo arranjo.
 * RELEASE: 2026-10-12 v2.0.0 - Layout compacto: cabeçalho em uma linha, blocos suaves no kit, campos vazios omitidos.
 */
export function StockInventoryCard({ item }: { item: StockInventoryItem }) {
  const Icon = item.kind === 'kit' ? Package : item.kind === 'tracker' ? Cpu : Smartphone;
  // Somente consulta: situação oficial em texto neutro, sem cápsula ou fundo colorido.
  const badge = (kind: 'kit' | 'tracker' | 'chip', value?: string | null) => <ConnectionStatusDot className="shrink-0 font-medium" label={value ? stockStatusLabel(kind, value) : 'Não informado'} dotClassName={!value ? 'bg-muted-foreground' : isStockStatusOperational(kind, value) ? 'bg-success' : 'bg-destructive'} />;
  const tag = <span className="shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase">{STOCK_ITEM_LABEL[item.kind]}</span>;
  const extra = [item.carrier_name, item.line_number].filter(Boolean).join(' · ');
  const date = inventoryDate(item.since);
  // Linha de situação própria: rótulo fixo + bolinha colorida + palavra (padrão BR-PP-018).
  const statusLine = <p className="flex items-center gap-1.5 text-foreground"><span className="text-muted-foreground">Status:</span>{badge(item.kind, item.status)}</p>;
  return <article className={cn(PROVIDER_DASHBOARD_CARD, 'border p-3')}>
    {/* Cabeçalho limpo: ícone + etiqueta + identificador em largura total, sem selo na mesma linha. */}
    <div className="flex items-center gap-2"><Icon className={cn('h-4 w-4 shrink-0', item.kind === 'chip' ? 'text-success' : 'text-primary')} />{tag}
      {item.kind === 'kit' ? <span className="min-w-0 flex-1 truncate font-mono text-sm font-semibold">{item.code}</span>
        : <InventoryIdentifier className="flex-1" label={item.kind === 'tracker' ? 'IMEI' : 'ICCID'} value={item.kind === 'tracker' ? item.imei : item.iccid} />}</div>
    <div className="mt-1.5 space-y-1 pl-6 text-xs text-muted-foreground">
      {/* Kit: situação própria na linha de baixo, seguida dos blocos de rastreador e chip. */}
      {item.kind === 'kit' && statusLine}
      {item.kind === 'kit' && <div className="space-y-1.5 pt-0.5">
        <div className="rounded-md bg-info/10 px-2.5 py-2"><div className="flex items-center gap-1.5"><Cpu className="h-3.5 w-3.5 shrink-0 text-info" /><span className="shrink-0 text-xs font-semibold text-info">Rastreador</span><InventoryIdentifier label="IMEI" value={item.imei} className="flex-1" /></div><p className="mt-0.5">{item.tracker_model_name || 'Modelo não informado'}</p><p className="mt-1 flex items-center gap-1.5 text-foreground"><span className="text-muted-foreground">Status:</span>{badge('tracker', item.tracker_status)}</p></div>
        <div className="rounded-md bg-success/10 px-2.5 py-2"><div className="flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5 shrink-0 text-success" /><span className="shrink-0 text-xs font-semibold text-success">Chip</span><InventoryIdentifier label="ICCID" value={item.iccid} className="flex-1" /></div><p className="mt-0.5 break-words">{[item.chip_data_plan_name, extra].filter(Boolean).join(' · ') || 'Plano não informado'}</p><p className="mt-1 flex items-center gap-1.5 text-foreground"><span className="text-muted-foreground">Status:</span>{badge('chip', item.chip_status)}</p></div>
      </div>}
      {item.kind === 'tracker' && <p>Modelo: {item.tracker_model_name || 'Não informado'}</p>}
      {item.kind === 'chip' && <>{item.chip_data_plan_name && <p className="break-words">Plano: {item.chip_data_plan_name}</p>}{extra && <p>{extra}</p>}</>}
      {item.kind !== 'kit' && statusLine}
      {date && <p className="text-[11px]">Última movimentação: {date}</p>}
    </div>
  </article>;
}

